import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { SessionUser } from '@swg/contracts';
import { fetchAuthMe } from './api.js';
import { UserContext } from './user-context.js';
import { AppShell } from './components/AppShell.js';
import { Login } from './pages/Login.js';
import { Dashboard } from './pages/Dashboard.js';
import { AnalysisDetail } from './pages/AnalysisDetail.js';
import { Users } from './pages/Users.js';
import { theme } from './theme.js';

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: SessionUser };

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchAuthMe()
      .then((res) => {
        if (cancelled) return;
        setAuth(
          res.authenticated && res.user
            ? { status: 'authenticated', user: res.user }
            : { status: 'anonymous' },
        );
      })
      .catch(() => {
        if (!cancelled) setAuth({ status: 'anonymous' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.status === 'loading') return <FullScreenSpinner />;
  if (auth.status === 'anonymous') return <Login />;

  return (
    <UserContext.Provider value={auth.user}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Dashboard />} />
          <Route path="analyses/:id" element={<AnalysisDetail />} />
          <Route path="users" element={<Users />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </UserContext.Provider>
  );
}

function FullScreenSpinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        aria-label="Laden"
        style={{
          width: 34,
          height: 34,
          border: `3px solid ${theme.color.border}`,
          borderTopColor: theme.color.primary,
          borderRadius: '50%',
          animation: 'swg-spin 0.9s linear infinite',
        }}
      />
    </div>
  );
}
